import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { Download, PackageSearch } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable } from "@/components/DataTable";
import { StatusPill } from "@/components/StatusPill";
import { StateFilterSelect } from "@/components/StateFilterSelect";
import { InlineDetailPanel } from "@/components/InlineDetailPanel";
import { DetailField } from "@/components/DetailDrawer";
import { adminApi, RETURN_STATES, type GetReturnOutput, type ReturnRow } from "@/lib/api";
import { LIST_STALE_MS, queryKeys } from "@/lib/queries";
import { queryTableProps } from "@/lib/data-table";
import { returnStateTone } from "@/lib/status-tone";
import { formatLondon, isoUtc } from "@/lib/format-time";
import { downloadCsv } from "@/lib/csv";

export const Route = createFileRoute("/_authed/returns")({
  component: ReturnsPage,
});

const METHOD_TONE: Record<string, "info" | "success" | "warning"> = {
  postal: "info",
  collect: "success",
  school: "warning",
};

/**
 * The fields the panel can show BEFORE the `adminGetReturn` fetch lands —
 * everything the list row already carries. Seeded as `placeholderData` so a
 * row click shows these immediately instead of a spinner; `report` stays
 * `null` (its fields, e.g. `finderLocation`/`notifiedAt`, are only known
 * server-side) until the real fetch resolves.
 */
function placeholderFromRow(row: ReturnRow): GetReturnOutput {
  return {
    id: row.id,
    request: {
      returnId: row.returnId,
      itemId: row.itemId,
      itemName: row.itemName,
      ownerUid: row.ownerUid,
      finderUid: row.finderUid,
      isAnonymousReport: row.isAnonymousReport,
      returnMethod: row.returnMethod,
      state: row.state,
      finderIntent: row.finderIntent,
      finderLocationLabel: row.finderLocationLabel,
      handedInTo: row.handedInTo,
      proposedSchoolName: row.proposedSchoolName,
      createdAt: row.createdAt,
      updatedAt: row.updatedAt,
    },
    report: null,
  };
}

function ReturnsPage() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Returns</h1>
        <p className="text-sm text-muted-foreground">
          The finder-report → owner-return lifecycle (returnRequests). Audit-logged.
        </p>
      </div>

      <ReturnsCard />
    </div>
  );
}

function ReturnsCard() {
  const [state, setState] = useState<string>("");
  const [detailRow, setDetailRow] = useState<ReturnRow | null>(null);
  const detailId = detailRow?.id ?? null;

  const query = useInfiniteQuery({
    queryKey: queryKeys.returns.list({ state }),
    queryFn: async ({ pageParam }) => {
      const res = await adminApi.listReturns()({
        ...(state ? { state: state as (typeof RETURN_STATES)[number] } : {}),
        cursor: pageParam,
      });
      return res.data;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    staleTime: LIST_STALE_MS,
  });

  const rows = query.data?.pages.flatMap((p) => p.returns) ?? [];
  const totalCount = query.data?.pages[0]?.totalCount ?? 0;

  const exportAnalytics = useMutation({
    mutationFn: async () => (await adminApi.exportReturnAnalytics()({})).data,
    onSuccess: ({ rows: exportRows }) => {
      const columns = Array.from(new Set(exportRows.flatMap((r) => Object.keys(r))));
      const yyyymmdd = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      downloadCsv(`return-analytics-${yyyymmdd}.csv`, exportRows, columns);
    },
  });

  const detail = useQuery({
    queryKey: queryKeys.returns.detail(detailId ?? "none"),
    queryFn: async () => {
      const res = await adminApi.getReturn()({ id: detailId! });
      return res.data;
    },
    enabled: !!detailId,
    placeholderData: detailRow ? placeholderFromRow(detailRow) : undefined,
  });

  const columns: ColumnDef<ReturnRow, unknown>[] = [
    {
      header: "Return",
      cell: ({ row }) => (
        <div className="space-y-0.5">
          <div className="font-mono text-xs">{row.original.returnId}</div>
          <div className="text-[11px] text-muted-foreground">
            {row.original.itemName ?? "—"}
          </div>
        </div>
      ),
    },
    {
      header: "Method",
      cell: ({ row }) => (
        <StatusPill tone={METHOD_TONE[row.original.returnMethod] ?? "neutral"}>
          {row.original.returnMethod}
        </StatusPill>
      ),
    },
    {
      header: "State",
      cell: ({ row }) => (
        <StatusPill tone={returnStateTone(row.original.state)}>{row.original.state}</StatusPill>
      ),
    },
    {
      header: "Intent",
      cell: ({ row }) => row.original.finderIntent ?? "—",
    },
    {
      header: "Anonymous?",
      cell: ({ row }) => (row.original.isAnonymousReport ? "Yes" : "No"),
    },
    {
      header: "Created",
      cell: ({ row }) => (
        <span title={isoUtc(row.original.createdAt)}>{formatLondon(row.original.createdAt)}</span>
      ),
    },
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <CardTitle className="flex items-center gap-2">
            <PackageSearch className="h-5 w-5" />
            Returns
            <span className="text-sm font-normal text-muted-foreground">
              {totalCount} total
            </span>
          </CardTitle>
          <div className="flex items-center gap-2">
            <StateFilterSelect value={state} onChange={setState} options={RETURN_STATES} allLabel="All states" />
            <Button
              variant="outline"
              size="sm"
              disabled={exportAnalytics.isPending}
              onClick={() => exportAnalytics.mutate()}
            >
              <Download className="mr-1 h-4 w-4" />
              {exportAnalytics.isPending ? "Exporting…" : "Export analytics CSV"}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {exportAnalytics.isError ? (
          <div className="rounded border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
            {(exportAnalytics.error as Error).message}
          </div>
        ) : null}
        <DataTable
          data={rows}
          columns={columns}
          isFetchingMore={query.isFetchingNextPage}
          hasMore={Boolean(query.hasNextPage)}
          onLoadMore={() => void query.fetchNextPage()}
          onRowClick={(row) => setDetailRow(row.id === detailId ? null : row)}
          emptyMessage="No returns yet."
          rowKey={(r) => r.id}
          footerNote="Click a row for the full return detail."
          {...queryTableProps(query)}
        />

        {detailRow && (
          <InlineDetailPanel
            title={`Return ${detailRow.id}`}
            onClose={() => setDetailRow(null)}
            isLoading={detail.isLoading}
            error={detail.error as Error | null}
          >
            <ReturnDetail
              request={detail.data?.request ?? null}
              report={detail.data?.report ?? null}
              pending={detail.isPlaceholderData}
            />
          </InlineDetailPanel>
        )}
      </CardContent>
    </Card>
  );
}

function ReturnDetail({
  request,
  report,
  pending,
}: {
  request: Record<string, unknown> | null;
  report: Record<string, unknown> | null;
  /** True while showing `placeholderData` — fields only `report` carries are unknown, not empty. */
  pending: boolean;
}) {
  // The owner-facing mirror carries the current lifecycle state; the finder's
  // original report is the only source of `notifiedAt` and (pre-merge)
  // `finderContact` (already masked server-side). Merge with `request`
  // winning on overlapping keys.
  const merged: Record<string, unknown> = { ...(report ?? {}), ...(request ?? {}) };

  const finderLocation = merged.finderLocation as { lat: number; lng: number } | null | undefined;
  const createdAt = merged.createdAt as number | null | undefined;
  const notifiedAt = (report?.notifiedAt as number | null | undefined) ?? null;

  const proposedSchool = merged.proposedSchoolName
    ? `${merged.proposedSchoolName as string}${merged.proposedMethod ? ` (${merged.proposedMethod as string})` : ""}`
    : null;

  return (
    <div className="space-y-4">
      <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <DetailField label="Return method" value={(merged.returnMethod as string) ?? "—"} />
        <DetailField label="State" value={(merged.state as string) ?? "—"} />
        <DetailField label="Finder intent" value={(merged.finderIntent as string) ?? "—"} />
        <DetailField label="Location label" value={(merged.finderLocationLabel as string) ?? "—"} />
        <DetailField label="Handed in to" value={(merged.handedInTo as string) ?? "—"} />
        <DetailField
          label="Finder location"
          value={
            finderLocation ? (
              <a
                className="text-primary underline"
                href={`https://www.google.com/maps?q=${finderLocation.lat},${finderLocation.lng}`}
                target="_blank"
                rel="noreferrer"
              >
                {finderLocation.lat.toFixed(5)}, {finderLocation.lng.toFixed(5)}
              </a>
            ) : pending ? (
              "Loading…"
            ) : (
              "—"
            )
          }
        />
        <DetailField
          label="Anonymous report?"
          value={merged.isAnonymousReport ? "Yes" : "No"}
        />
        <DetailField label="Proposed school" value={proposedSchool ?? "—"} />
        <DetailField
          label="Created"
          value={
            createdAt ? (
              <span title={isoUtc(createdAt)}>{formatLondon(createdAt)}</span>
            ) : (
              "—"
            )
          }
        />
        <DetailField
          label="Notified"
          value={
            notifiedAt ? (
              <span title={isoUtc(notifiedAt)}>{formatLondon(notifiedAt)}</span>
            ) : pending ? (
              "Loading…"
            ) : (
              "—"
            )
          }
        />
      </dl>

      <details className="rounded-md border bg-muted">
        <summary className="cursor-pointer px-3 py-2 text-xs font-medium text-muted-foreground">
          Raw JSON
        </summary>
        <pre className="max-h-96 overflow-auto border-t px-3 py-2 text-xs">
          {JSON.stringify({ request, report }, null, 2)}
        </pre>
      </details>
    </div>
  );
}
